import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { isSupabaseEnabled, supabase } from '@/lib/supabase';
import { useAppEnvironment } from '@/hooks/AppEnvironmentContext';
import type { User, Session } from '@supabase/supabase-js';

export interface AppUser {
  id: string;
  email: string;
  user_metadata: Record<string, string>;
}

interface AuthContextValue {
  user: AppUser | null;
  session: Session | null;
  accessToken: string | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, metadata: Record<string, string>) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return ctx;
}

function toAppUser(u: User): AppUser {
  return {
    id: u.id,
    email: u.email ?? '',
    user_metadata: (u.user_metadata as Record<string, string>) ?? {},
  };
}

interface StorefrontAuthUser {
  id: string;
  email: string | null;
  user_metadata: Record<string, string>;
}

interface StorefrontAuthState {
  accessToken: string;
  user: AppUser;
}

interface TelegramMiniAppAuthResponse {
  access_token: string;
  user: StorefrontAuthUser;
}

const storefrontAuthStorageKey = 'pchelovod-storefront-auth';

function apiBaseUrl() {
  return (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
}

function decodeBase64Url(value: string) {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return atob(padded);
}

function isJwtExpired(token: string) {
  try {
    const [, payload] = token.split('.');
    if (!payload) return true;
    const data = JSON.parse(decodeBase64Url(payload)) as { exp?: number };
    if (!data.exp) return true;
    return data.exp <= Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
}

function readStoredStorefrontAuth(): StorefrontAuthState | null {
  try {
    const raw = localStorage.getItem(storefrontAuthStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StorefrontAuthState;
    if (!parsed.accessToken || !parsed.user?.id) return null;
    if (isJwtExpired(parsed.accessToken)) {
      localStorage.removeItem(storefrontAuthStorageKey);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredStorefrontAuth(auth: StorefrontAuthState | null) {
  try {
    if (!auth) {
      localStorage.removeItem(storefrontAuthStorageKey);
      return;
    }
    localStorage.setItem(storefrontAuthStorageKey, JSON.stringify(auth));
  } catch {
    // Browser storage is best-effort; in-memory auth still works for the current session.
  }
}

async function authenticateTelegramMiniApp(initData: string, signal: AbortSignal): Promise<StorefrontAuthState> {
  const baseUrl = apiBaseUrl();
  if (!baseUrl) throw new Error('VITE_API_BASE_URL is not configured');

  const response = await fetch(`${baseUrl}/auth/telegram-miniapp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ init_data: initData }),
    signal,
  });
  if (!response.ok) {
    throw new Error(`Telegram auth failed: ${response.status}`);
  }

  const data = (await response.json()) as TelegramMiniAppAuthResponse;
  return {
    accessToken: data.access_token,
    user: {
      id: data.user.id,
      email: data.user.email ?? '',
      user_metadata: data.user.user_metadata ?? {},
    },
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const environment = useAppEnvironment();
  const [realUser, setRealUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [storefrontAuth, setStorefrontAuth] = useState<StorefrontAuthState | null>(() => readStoredStorefrontAuth());
  const [demoUser, setDemoUser] = useState<AppUser | null>(null);
  const [supabaseLoading, setSupabaseLoading] = useState(isSupabaseEnabled);
  const [telegramLoading, setTelegramLoading] = useState(() => environment.isTelegram && Boolean(environment.telegram.initData));

  useEffect(() => {
    if (!isSupabaseEnabled || !supabase) {
      setSupabaseLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setRealUser(data.session?.user ?? null);
      setSupabaseLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
        setRealUser(newSession?.user ?? null);
        setSupabaseLoading(false);
      }
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!environment.isTelegram) {
      setTelegramLoading(false);
      return;
    }

    if (!environment.telegram.initData) {
      setTelegramLoading(false);
      return;
    }

    const controller = new AbortController();
    setTelegramLoading(true);
    authenticateTelegramMiniApp(environment.telegram.initData, controller.signal)
      .then((auth) => {
        setStorefrontAuth(auth);
        writeStoredStorefrontAuth(auth);
      })
      .catch(() => {
        setStorefrontAuth(null);
        writeStoredStorefrontAuth(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setTelegramLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [environment.isTelegram, environment.telegram.initData]);

  const user = demoUser ?? storefrontAuth?.user ?? (realUser ? toAppUser(realUser) : null);
  const accessToken = storefrontAuth?.accessToken ?? session?.access_token ?? null;
  const isLoading = supabaseLoading || telegramLoading;

  const signIn = useCallback(async (_email: string, _password: string) => {
    // Demo mode: accept any credentials for testing
    setDemoUser({
      id: 'demo-' + Date.now(),
      email: _email,
      user_metadata: {},
    });
    return {};
  }, []);

  const signUp = useCallback(
    async (_email: string, _password: string, metadata: Record<string, string>) => {
      // Demo mode: accept any registration data for testing
      setDemoUser({
        id: 'demo-' + Date.now(),
        email: _email,
        user_metadata: metadata,
      });
      return {};
    },
    []
  );

  const signOut = useCallback(async () => {
    setDemoUser(null);
    setStorefrontAuth(null);
    writeStoredStorefrontAuth(null);
    if (supabase) {
      try {
        await supabase.auth.signOut();
      } catch {
        // ignore if no real session
      }
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, session, accessToken, isLoading, signIn, signUp, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}
